import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agendaPublicService } from '../../services/agendaPublicService';
import {
    FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUser, FaSearch,
    FaArrowLeft, FaSpinner, FaExclamationTriangle, FaEye,
    FaStar, FaFilePdf, FaDownload, FaTimes, FaChevronLeft,
    FaChevronRight,
    // Ikon Share
    FaFacebookF, FaTwitter, FaWhatsapp, FaLinkedinIn,
    FaTelegramPlane, FaLink, FaCheck, FaShareAlt
} from 'react-icons/fa';

const kategoriOptions = [
    { value: 'semua', label: 'Semua' },
    { value: 'umum', label: 'Umum' },
    { value: 'rapat', label: 'Rapat' },
    { value: 'pelatihan', label: 'Pelatihan' },
    { value: 'sosialisasi', label: 'Sosialisasi' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'lainnya', label: 'Lainnya' },
];

const statusKegiatanOptions = [
    { value: 'semua', label: 'Semua' },
    { value: 'akan_datang', label: 'Akan Datang' },
    { value: 'berlangsung', label: 'Berlangsung' },
    { value: 'selesai', label: 'Selesai' },
];

const AgendaPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [agendas, setAgendas] = useState([]);
    const [selectedAgenda, setSelectedAgenda] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterKategori, setFilterKategori] = useState('semua');
    const [filterStatus, setFilterStatus] = useState('semua');
    const [filterWaktu, setFilterWaktu] = useState('semua');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (slug) {
            loadDetail(slug);
        } else {
            setSelectedAgenda(null);
            loadAgendas(1);
        }
    }, [slug, filterKategori, filterStatus, filterWaktu, searchTerm]);

    const loadAgendas = async (page = 1) => {
        setLoading(true);
        setError('');

        const params = { page, per_page: 9 };
        if (filterKategori !== 'semua') params.kategori = filterKategori;
        if (filterStatus !== 'semua') params.status_kegiatan = filterStatus;
        if (filterWaktu !== 'semua') params.waktu = filterWaktu;
        if (searchTerm) params.search = searchTerm;

        const result = await agendaPublicService.getAgendas(params);
        if (result.success) {
            setAgendas(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const loadDetail = async (slug) => {
        setLoadingDetail(true);
        const result = await agendaPublicService.getAgendaBySlug(slug);
        if (result.success) {
            setSelectedAgenda(result.data.data || result.data);
            window.scrollTo(0, 0);
        } else {
            setError(result.message);
        }
        setLoadingDetail(false);
    };

    const handleAgendaClick = (agenda) => {
        navigate(`/informasi/agenda/${agenda.slug}`);
    };

    const handleBackToList = () => {
        navigate('/informasi/agenda');
        window.scrollTo(0, 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'akan_datang': return 'bg-blue-100 text-blue-700';
            case 'berlangsung': return 'bg-green-100 text-green-700';
            case 'selesai': return 'bg-gray-100 text-gray-600';
            case 'dibatalkan': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getStatusLabel = (status) => {
        return statusKegiatanOptions.find(o => o.value === status)?.label || status;
    };

    // ============ FUNGSI SHARE ============
    const getAgendaUrl = () => {
        return window.location.href;
    };

    const handleShare = (platform) => {
        const url = encodeURIComponent(getAgendaUrl());
        const title = encodeURIComponent(selectedAgenda?.judul || 'Agenda BPKAD Kota Magelang');

        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
            whatsapp: `https://wa.me/?text=${title}%20${url}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
            telegram: `https://t.me/share/url?url=${url}&text=${title}`,
        };

        if (platform === 'copy') {
            navigator.clipboard.writeText(getAgendaUrl()).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch((err) => {
                console.error('Gagal menyalin link:', err);
            });
            return;
        }

        window.open(shareUrls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    // ============ DETAIL VIEW ============
    if (slug && selectedAgenda) {
        return (
            <div className="container mx-auto px-4 py-12">
                <button
                    onClick={handleBackToList}
                    className="flex items-center text-[#2E7D32] hover:text-[#4CAF50] mb-6 font-medium"
                >
                    <FaArrowLeft className="mr-2" /> Kembali ke Daftar Agenda
                </button>

                <article className="max-w-7xl mx-auto">
                    {selectedAgenda.gambar && (
                        <img
                            src={selectedAgenda.gambar}
                            alt={selectedAgenda.judul}
                            className="w-full h-64 md:h-96 object-cover rounded-xl mb-6 shadow-lg"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedAgenda.status_kegiatan)}`}>
                            {getStatusLabel(selectedAgenda.status_kegiatan)}
                        </span>
                        <span className="bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1 rounded-full text-xs font-medium capitalize">
                            {selectedAgenda.kategori}
                        </span>
                        {selectedAgenda.is_penting && (
                            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                <FaStar className="mr-1" /> Penting
                            </span>
                        )}
                    </div>

                    <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A] mb-6">
                        {selectedAgenda.judul}
                    </h1>

                    {/* ============ SHARE TO SOCIAL MEDIA (ATAS) ============ */}
                    <div className="flex flex-wrap items-center gap-3 py-4 mb-6 border-y border-gray-200">
                        <span className="flex items-center text-gray-600 font-semibold text-sm">
                            <FaShareAlt className="mr-2 text-[#2E7D32]" /> Bagikan:
                        </span>

                        <button
                            onClick={() => handleShare('facebook')}
                            aria-label="Bagikan ke Facebook"
                            title="Bagikan ke Facebook"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaFacebookF />
                        </button>

                        <button
                            onClick={() => handleShare('twitter')}
                            aria-label="Bagikan ke Twitter"
                            title="Bagikan ke Twitter"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaTwitter />
                        </button>

                        <button
                            onClick={() => handleShare('whatsapp')}
                            aria-label="Bagikan ke WhatsApp"
                            title="Bagikan ke WhatsApp"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaWhatsapp />
                        </button>

                        <button
                            onClick={() => handleShare('linkedin')}
                            aria-label="Bagikan ke LinkedIn"
                            title="Bagikan ke LinkedIn"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaLinkedinIn />
                        </button>

                        <button
                            onClick={() => handleShare('telegram')}
                            aria-label="Bagikan ke Telegram"
                            title="Bagikan ke Telegram"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaTelegramPlane />
                        </button>

                        <button
                            onClick={() => handleShare('copy')}
                            aria-label="Salin link agenda"
                            title="Salin link"
                            className={`w-10 h-10 flex items-center justify-center rounded-full text-white hover:scale-110 hover:shadow-lg transition-all duration-200 ${copied ? 'bg-green-600' : 'bg-gray-600'
                                }`}
                        >
                            {copied ? <FaCheck /> : <FaLink />}
                        </button>

                        {copied && (
                            <span className="text-sm text-green-600 font-medium animate-pulse">
                                Link berhasil disalin!
                            </span>
                        )}
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-start gap-3">
                            <FaCalendarAlt className="text-[#2E7D32] mt-1 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-gray-500">Tanggal</p>
                                <p className="font-semibold text-gray-800">
                                    {formatDate(selectedAgenda.tanggal_mulai)}
                                    {selectedAgenda.tanggal_selesai &&
                                        selectedAgenda.tanggal_selesai !== selectedAgenda.tanggal_mulai &&
                                        ` - ${formatDate(selectedAgenda.tanggal_selesai)}`}
                                </p>
                            </div>
                        </div>

                        {(selectedAgenda.jam_mulai || selectedAgenda.jam_selesai) && (
                            <div className="flex items-start gap-3">
                                <FaClock className="text-[#2E7D32] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500">Waktu</p>
                                    <p className="font-semibold text-gray-800">
                                        {selectedAgenda.jam_mulai || '-'}
                                        {selectedAgenda.jam_selesai && ` - ${selectedAgenda.jam_selesai}`}
                                    </p>
                                </div>
                            </div>
                        )}

                        {selectedAgenda.lokasi && (
                            <div className="flex items-start gap-3">
                                <FaMapMarkerAlt className="text-[#2E7D32] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500">Lokasi</p>
                                    <p className="font-semibold text-gray-800">{selectedAgenda.lokasi}</p>
                                </div>
                            </div>
                        )}

                        {selectedAgenda.penyelenggara && (
                            <div className="flex items-start gap-3">
                                <FaUser className="text-[#2E7D32] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500">Penyelenggara</p>
                                    <p className="font-semibold text-gray-800">{selectedAgenda.penyelenggara}</p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-3">
                            <FaEye className="text-[#2E7D32] mt-1 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-gray-500">Views</p>
                                <p className="font-semibold text-gray-800">{selectedAgenda.views || 0}</p>
                            </div>
                        </div>
                    </div>

                    {selectedAgenda.deskripsi && (
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 italic border-l-4 border-[#2E7D32] pl-4">
                            {selectedAgenda.deskripsi}
                        </p>
                    )}

                    {selectedAgenda.konten && (
                        <div className="prose max-w-none">
                            <div
                                className="text-gray-700 leading-relaxed break-words 
                                            [&>p]:mb-4 [&>p]:leading-7 
                                            [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-2xl [&>h2]:font-bold
                                            [&>ul]:list-disc [&>ul]:ml-5 [&>ul]:mb-4
                                            prose-headings:text-[#0F4C3A] prose-a:text-[#2E7D32] prose-img:rounded-lg"
                                dangerouslySetInnerHTML={{ __html: selectedAgenda.konten }}
                            />
                        </div>
                    )}

                    {selectedAgenda.file_lampiran && (
                        <div className="mt-8 p-4 bg-gray-50 rounded-lg border">
                            <h3 className="font-semibold text-[#0F4C3A] mb-3 flex items-center">
                                <FaFilePdf className="mr-2 text-red-500" />
                                File Lampiran
                            </h3>
                            <a
                                href={selectedAgenda.file_lampiran}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-100 transition-colors border"
                            >
                                <span className="text-sm text-gray-700 truncate">
                                    {selectedAgenda.file_name || 'Download'}
                                </span>
                                <FaDownload className="text-[#2E7D32]" />
                            </a>
                        </div>
                    )}

                    {/* ============ SHARE BAWAH ============ */}
                    <div className="mt-10 pt-6 border-t border-gray-200">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-gray-600 font-semibold text-sm">
                                Bagikan agenda ini ke:
                            </span>
                            <button
                                onClick={() => handleShare('facebook')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-all"
                                title="Facebook"
                            >
                                <FaFacebookF size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('twitter')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 transition-all"
                                title="Twitter"
                            >
                                <FaTwitter size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('whatsapp')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 transition-all"
                                title="WhatsApp"
                            >
                                <FaWhatsapp size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('telegram')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 transition-all"
                                title="Telegram"
                            >
                                <FaTelegramPlane size={14} />
                            </button>
                        </div>
                    </div>
                </article>
            </div>
        );
    }

    // ============ LIST VIEW ============
    return (
        <div className="container mx-auto px-4 py-12">
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                    <FaCalendarAlt className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            Agenda Kegiatan
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Jadwal dan agenda kegiatan BPKAD Kota Magelang
                        </p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari agenda..."
                        className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] outline-none"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <select
                    value={filterWaktu}
                    onChange={(e) => setFilterWaktu(e.target.value)}
                    className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="semua">Semua Waktu</option>
                    <option value="akan_datang">Akan Datang</option>
                    <option value="berlangsung">Berlangsung</option>
                    <option value="selesai">Selesai</option>
                </select>
                <select
                    value={filterKategori}
                    onChange={(e) => setFilterKategori(e.target.value)}
                    className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    {kategoriOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="semua">Semua Status</option>
                    {statusKegiatanOptions.slice(1).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Content */}
            {loading ? (
                <div className="text-center py-20">
                    <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                    <p className="text-gray-500">Memuat agenda...</p>
                </div>
            ) : error ? (
                <div className="text-center py-20">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">{error}</p>
                    <button onClick={() => loadAgendas(1)} className="btn-primary">Coba Lagi</button>
                </div>
            ) : agendas.length === 0 ? (
                <div className="text-center py-20">
                    <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-500 mb-2">Tidak Ada Agenda</h3>
                    <p className="text-gray-400">Belum ada agenda untuk filter yang dipilih</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {agendas.map((agenda) => (
                            <div
                                key={agenda.id}
                                className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group"
                                onClick={() => handleAgendaClick(agenda)}
                            >
                                <div className="relative overflow-hidden h-48">
                                    {agenda.gambar ? (
                                        <img
                                            src={agenda.gambar}
                                            alt={agenda.judul}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            onError={(e) => {
                                                e.target.src = 'https://placehold.co/400x300/0F4C3A/FFFFFF?text=Agenda';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                            <FaCalendarAlt className="text-5xl text-white/50" />
                                        </div>
                                    )}

                                    {/* Date Badge */}
                                    <div className="absolute top-3 left-3 bg-white rounded-lg shadow-lg px-3 py-2 text-center">
                                        <p className="text-2xl font-bold text-[#0F4C3A]">
                                            {new Date(agenda.tanggal_mulai).getDate()}
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase">
                                            {new Date(agenda.tanggal_mulai).toLocaleDateString('id-ID', { month: 'short' })}
                                        </p>
                                    </div>

                                    {/* Status Badge */}
                                    <div className="absolute top-3 right-3">
                                        {agenda.is_penting && (
                                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                                <FaStar className="mr-1" /> Penting
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agenda.status_kegiatan)}`}>
                                            {getStatusLabel(agenda.status_kegiatan)}
                                        </span>
                                        <span className="bg-[#2E7D32]/10 text-[#2E7D32] px-2 py-1 rounded-full text-xs font-medium capitalize">
                                            {agenda.kategori}
                                        </span>
                                    </div>

                                    <h3 className="text-lg font-bold text-[#0F4C3A] mb-2 group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                                        {agenda.judul}
                                    </h3>

                                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                        {agenda.deskripsi}
                                    </p>

                                    <div className="space-y-1 text-xs text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <FaCalendarAlt className="text-[#2E7D32]" />
                                            <span>{formatShortDate(agenda.tanggal_mulai)}</span>
                                        </div>
                                        {agenda.jam_mulai && (
                                            <div className="flex items-center gap-2">
                                                <FaClock className="text-[#2E7D32]" />
                                                <span>{agenda.jam_mulai} WIB</span>
                                            </div>
                                        )}
                                        {agenda.lokasi && (
                                            <div className="flex items-center gap-2">
                                                <FaMapMarkerAlt className="text-[#2E7D32]" />
                                                <span className="line-clamp-1">{agenda.lokasi}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-10">
                            <button
                                onClick={() => loadAgendas(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                            >
                                <FaChevronLeft />
                            </button>
                            <span className="px-4 py-2 text-gray-600">
                                Halaman {currentPage} dari {totalPages}
                            </span>
                            <button
                                onClick={() => loadAgendas(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}

            {loadingDetail && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl p-6 text-center shadow-2xl">
                        <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto mb-2" />
                        <p className="text-gray-500">Memuat agenda...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgendaPage;